import { FeatureGate } from "@/components/molecules/FeatureGate/FeatureGate";
import type { EnergyPlot } from "@/energyPlotView/types";
import { HighlightType, useActiveGridHighlight } from "@/grid/GridCellHighlightProvider";
import { compareGridCellLayouts } from "@/grid/useCurrentLayout";
import { useProfileAndTabParams } from "@/hooks";
import { type ComponentFactoryProps, LayerStack, type WindowElement, useDesktop } from "@/overlay/components";
import { EditableTextbox } from "@/overlay/components/CommonParts/EditableTextbox";
import { Button, ButtonBar } from "@/overlay/components/CommonParts/buttons";
import { LocationTableSettings } from "@/overlay/components/LayerStackWindow/LocationTableSettings";
import { MapProjectionPanel } from "@/overlay/components/LayerStackWindow/MapProjectionPanel";
import { FormField } from "@/overlay/components/form-field";
import * as windowManager from "@/overlay/components/window";
import { WindowKind, instanceId } from "@/overlay/components/window";
import { DragItemType } from "@/overlay/components/window/DragItemType";
import { type RootState, useAppDispatch } from "@/reducer";
import { discardEnergyPlot, useEnergyPlots } from "@/reducer/EnergyPlotsState";
import { useLayouts } from "@/reducer/LayoutsState";
import { type LocationTableState, discardLocationTable, useLocationTables } from "@/reducer/LocationTablesState";
import { discardMap, setMapProjectionProps, setMapTitleProps, useMaps } from "@/reducer/MapsState";
import { discardNote } from "@/reducer/NoteState";
import { discardPlot, usePlots } from "@/reducer/PlotsState";
import { discardTephigram, useTephigrams } from "@/reducer/TephigramState";
import { setLayoutArrangement, setSelectedMap } from "@/reducer/UIState";
import { type WeatherTableState, discardWeatherTable, useWeatherTables } from "@/reducer/WeatherTablesState";
import type { Plot } from "@/reducer/client-models";
import { getToolByToolId } from "@/reducer/selectors/AccountSelectors";
import { t } from "@lingui/core/macro";
import {
  type CartographicMapOutput,
  type GenericLayout,
  LayoutType,
  type MapProjection,
  ProjectionNameEnum,
  type TephigramSchemaOutput,
} from "@mm/metx-workbench.meteomatics.com";
import { MmSwitch } from "@mm/zeus-react";
import { lazy, memo, useMemo } from "react";
import { useDragLayer } from "react-dnd";
import { useDispatch, useSelector } from "react-redux";
import { ComponentSwitcher } from "../CommonParts/ComponentSwitcher";
import { toolFormControllerMapping } from "../SmartForm/ToolsForms/ToolFormMapping";
import { v2ToolFormSupported } from "../SmartForm/ToolsForms/supportCheckerTools";
import { EnergyPlotSetting } from "./EnergyPlotSettings/EnergyPlotSettings";
import { MapActionPanel } from "./MapActionPanel/MapActionPanel";
import { TephigramSetting } from "./TephigramSettings";
import { WeatherTableSetting } from "./WeatherTableSettings";
import "./style.scss";
import { getLayerStackWindowStyle } from "./windowStyle";

export const DEFAULT_MAP_PROJECTION: MapProjection = {
  name: ProjectionNameEnum.mercator,
};

const PlotSetting = memo(
  lazy(() =>
    import(/* webpackChunkName: "plot-setting" */ "@/overlay/components/LayerStackWindow/PlotSetting")
      .then(({ PlotSetting }) => ({ default: PlotSetting }))
      .catch(() => {
        return { default: () => <div>Loading Plot Setting failed</div> };
      }),
  ),
);

export default function LayerStackWindow(props: ComponentFactoryProps<WindowKind.LayerStack>): WindowElement {
  const { profileId, tabId } = useProfileAndTabParams();
  const desktop = useDesktop();

  const maps = useMaps(tabId);

  const plots = usePlots(tabId);
  const energyPlots = useEnergyPlots(tabId);
  const weatherTables = useWeatherTables(tabId);
  const locationTables = useLocationTables(tabId);
  const tephigrams = useTephigrams(tabId);
  const layouts = useLayouts(tabId);
  const appDispatch = useAppDispatch();

  const dispatch = useDispatch();
  const { isDragging, itemType } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    itemType: monitor.getItemType(),
  }));
  const isLayoutArrangementActive = useSelector((state: RootState) => state.ui.isLayoutArrangementActive);
  const windowStyle = getLayerStackWindowStyle({
    draggableArea: desktop.dragableArea,
    floatingBottomOffset: props.floatingBottomOffset,
    position: props.position,
    style: props.style,
  });

  const sortedLayouts = useMemo(() => {
    if (layouts) {
      return [...layouts].sort((a, b) => compareGridCellLayouts(a.gridCellLayout, b.gridCellLayout));
    }
    return [];
  }, [layouts]);

  const { setHighlighted } = useActiveGridHighlight();

  const removeLayoutContent = (layout: GenericLayout) => {
    if (layout.id_tool) {
      switch (layout.type) {
        case LayoutType.EnergyPlot:
          appDispatch(discardEnergyPlot({ id: layout.id_tool }));
          break;
        case LayoutType.Map:
          appDispatch(discardMap({ id: layout.id_tool }));
          break;
        case LayoutType.Plot:
          appDispatch(discardPlot({ id: layout.id_tool }));
          break;
        case LayoutType.WeatherTable:
          appDispatch(discardWeatherTable({ id: layout.id_tool }));
          break;
        case LayoutType.LocationTable:
          appDispatch(discardLocationTable({ id: layout.id_tool }));
          break;
        case LayoutType.Tephigram:
          appDispatch(discardTephigram({ id: layout.id_tool }));
          break;
        case LayoutType.Notes:
          appDispatch(discardNote({ id: layout.id_tool }));
          break;
        default:
          console.warn("Layout has no tool associated");
      }
    }
  };

  const actions = [
    windowManager.closeAction(() =>
      desktop.dispatch({ type: "setWindowVisibility", windowId: props.instanceId, isVisible: false }),
    ),
  ];

  function openAddLayerWindow(cartographicMap: CartographicMapOutput) {
    dispatch(setSelectedMap(cartographicMap));
    desktop.dispatch({ type: "setWindowVisibility", windowId: instanceId(WindowKind.AddLayer), isVisible: true });
  }

  function submitMapTitleUpdate(map: CartographicMapOutput, title: CartographicMapOutput["title"]) {
    dispatch(setMapTitleProps({ id: map.id, title: title }));
  }

  function updateMapProjection(map: CartographicMapOutput, propsValue: MapProjection) {
    dispatch(setMapProjectionProps({ id: map.id, props: propsValue }));
  }

  return (
    <windowManager.Window
      kind="layer-stack"
      title={t`Layer Stack`}
      actions={actions}
      {...props}
      className={isDragging && itemType === DragItemType.GridElement ? "drag-hidden" : ""}
      style={windowStyle}
    >
      <FormField label={t`Arrange mode`} className="layer-stack__arrange-mode">
        <MmSwitch
          name={"arrange"}
          sid={"arrange-toggle"}
          toggleHandler={() => {
            dispatch(setLayoutArrangement(!isLayoutArrangementActive));
          }}
          checked={isLayoutArrangementActive}
        />
      </FormField>

      <div className="layer-stack__list">
        {sortedLayouts.map((layout) => {
          if (v2ToolFormSupported(layout) && layout.id_tool) {
            return (
              <ComponentSwitcher
                key={layout.id_tool}
                componentProps={{ layout }}
                componentMapping={toolFormControllerMapping}
              />
            );
          }
          if (layout.type === LayoutType.Map && layout.id_tool && maps?.length > 0) {
            const cartographicMap = getToolByToolId<CartographicMapOutput>(layout.id_tool, maps);

            if (cartographicMap) {
              return (
                <FeatureGate key={`map-gate-${cartographicMap.id}`} path="tools.map.enabled">
                  <windowManager.WindowSection
                    key={`map-${cartographicMap.id}`}
                    onMouseEnter={() => {
                      setHighlighted({
                        type: HighlightType.MAP,
                        id: cartographicMap.id,
                      });
                    }}
                    onMouseLeave={() => {
                      setHighlighted(undefined);
                    }}
                  >
                    <div className="title-row">
                      <EditableTextbox
                        className={"editable-map-title"}
                        value={cartographicMap.title ?? ""}
                        blurOnEnterKey
                        onSave={(newTitle) => {
                          submitMapTitleUpdate(cartographicMap, newTitle);
                        }}
                      />
                      <MapActionPanel
                        id={{ profile: profileId, tab: tabId, map: cartographicMap.id }}
                        mapProjection={cartographicMap.map_projection ?? DEFAULT_MAP_PROJECTION}
                        clearMap={() => removeLayoutContent(layout)}
                      />
                    </div>

                    {/* TODO: We would like to place it later somewhere here, for now this is still on each wmslayer config form*/}
                    {/*<DataResolutionSettings*/}
                    {/*  id={{ profile: profileId, tab: tabId, map: cartographicMap.id }}*/}
                    {/*  lod_bias={cartographicMap.lod_bias ?? 0}*/}
                    {/*  disabled={*/}
                    {/*    !!cartographicMap.map_projection?.name &&*/}
                    {/*    cartographicMap.map_projection?.name !== ProjectionNameEnum.mercator*/}
                    {/*  }*/}
                    {/*/>*/}
                    <MapProjectionPanel
                      mapProjection={{ ...DEFAULT_MAP_PROJECTION, ...cartographicMap.map_projection }}
                      onProjectionUpdate={(fields) => {
                        updateMapProjection(cartographicMap, fields);
                      }}
                    />
                    <LayerStack
                      id={{ profile: profileId, tab: tabId, map: cartographicMap.id }}
                      cartographicMap={cartographicMap}
                    />
                    <ButtonBar align="right" className="layer-stack-actions">
                      <Button
                        data-testid={"add-layer-button"}
                        icon="add"
                        onClick={() => openAddLayerWindow(cartographicMap)}
                      >{t`Add Layer`}</Button>
                    </ButtonBar>
                  </windowManager.WindowSection>
                </FeatureGate>
              );
            }
          }
          if (layout.type === LayoutType.Plot && layout.id_tool && plots?.length > 0) {
            const plot = getToolByToolId<Plot>(layout.id_tool, plots);
            if (plot) {
              return (
                <FeatureGate key={`plot-gate-${plot.id}`} path="tools.plot.enabled">
                  <windowManager.WindowSection
                    key={`plot-${plot.id}`}
                    onMouseEnter={() => {
                      setHighlighted({
                        type: HighlightType.PLOT,
                        id: plot.id,
                      });
                    }}
                    onMouseLeave={() => {
                      setHighlighted(undefined);
                    }}
                  >
                    <PlotSetting plot={plot} clearPlot={() => removeLayoutContent(layout)} />
                  </windowManager.WindowSection>
                </FeatureGate>
              );
            }
          }
          if (layout.type === LayoutType.EnergyPlot && layout.id_tool && energyPlots && energyPlots.length > 0) {
            const energyPlot = getToolByToolId<EnergyPlot>(layout.id_tool, energyPlots);
            if (energyPlot) {
              return (
                <FeatureGate key={`energy-plot-gate-${energyPlot.id}`} path="tools.energy_plot.enabled">
                  <windowManager.WindowSection
                    key={`energy-plot-${energyPlot.id}`}
                    onMouseEnter={() => {
                      setHighlighted({
                        type: HighlightType.ENERGY_PLOT,
                        id: energyPlot.id,
                      });
                    }}
                    onMouseLeave={() => {
                      setHighlighted(undefined);
                    }}
                  >
                    <EnergyPlotSetting energyPlot={energyPlot} clearEnergyPlot={() => removeLayoutContent(layout)} />
                  </windowManager.WindowSection>
                </FeatureGate>
              );
            }
          }
          if (layout.type === LayoutType.WeatherTable && layout.id_tool && weatherTables && weatherTables.length > 0) {
            const weatherTable = getToolByToolId<WeatherTableState>(layout.id_tool, weatherTables);
            if (weatherTable) {
              return (
                <FeatureGate key={`weather-table-gate-${weatherTable.id}`} path="tools.table.enabled">
                  <windowManager.WindowSection
                    key={`weather-table-${weatherTable.id}`}
                    onMouseEnter={() => {
                      setHighlighted({
                        type: HighlightType.WEATHER_TABLE,
                        id: weatherTable.id,
                      });
                    }}
                    onMouseLeave={() => {
                      setHighlighted(undefined);
                    }}
                  >
                    <WeatherTableSetting
                      weatherTable={weatherTable}
                      clearWeatherTable={() => removeLayoutContent(layout)}
                    />
                  </windowManager.WindowSection>
                </FeatureGate>
              );
            }
          }
          if (
            layout.type === LayoutType.LocationTable &&
            layout.id_tool &&
            locationTables &&
            locationTables.length > 0
          ) {
            const locationTable = getToolByToolId<LocationTableState>(layout.id_tool, locationTables);
            if (locationTable) {
              return (
                <FeatureGate key={`location-table-gate-${locationTable.id}`} path="tools.table.enabled">
                  <windowManager.WindowSection
                    key={`location-table-${locationTable.id}`}
                    onMouseEnter={() => {
                      setHighlighted({
                        type: HighlightType.LOCATION_TABLE,
                        id: locationTable.id,
                      });
                    }}
                    onMouseLeave={() => {
                      setHighlighted(undefined);
                    }}
                  >
                    <LocationTableSettings
                      locationTable={locationTable}
                      clearTable={() => removeLayoutContent(layout)}
                    />
                  </windowManager.WindowSection>
                </FeatureGate>
              );
            }
          }
          if (layout.type === LayoutType.Tephigram && layout.id_tool && tephigrams && tephigrams.length > 0) {
            const tephigram = getToolByToolId<TephigramSchemaOutput>(layout.id_tool, tephigrams);
            if (tephigram) {
              return (
                <FeatureGate key={`tephigram-gate-${tephigram.id}`} path="tools.skewt.enabled">
                  <windowManager.WindowSection
                    key={`tephigram-${tephigram.id}`}
                    onMouseEnter={() => {
                      setHighlighted({
                        type: HighlightType.TEPHIGRAM,
                        id: tephigram.id,
                      });
                    }}
                    onMouseLeave={() => {
                      setHighlighted(undefined);
                    }}
                  >
                    <TephigramSetting tephigram={tephigram} clearTephigram={() => removeLayoutContent(layout)} />
                  </windowManager.WindowSection>
                </FeatureGate>
              );
            }
          }
          // biome-ignore lint/correctness/useJsxKeyInIterable: weird
          return undefined;
        })}
      </div>
    </windowManager.Window>
  );
}
